import Vue from 'vue'
import {onUnload, parseActiveDirectoryUserId} from 'src/utilities/utils'
import {make} from 'vuex-pathify'
import {OK} from 'http-status-codes'
import Constants from 'src/utilities/constants'
import {ptracGetEntity} from 'src/utilities/ptracApi'

let initialState = {
  user: {
    id: null,
    name: null,
    username: null,
    groups: [],
    claims: {},
    preferences: {},
    adminAppPreferences: {},
    savedTaskQueries:{},
    savedJobQueries: {}
  }
}

const state = Vue.util.extend({}, JSON.parse(JSON.stringify(initialState)))

const getters = make.getters(state)
const mutations = make.mutations(state)

getters.userId = (state) => {
  return state.user.id
}
getters.username = (state) => {
  return state.user.username
}
getters.name = (state) => {
  return state.user.name
}
getters.savedJobQueries = (state) => {
  return state.user.savedJobQueries
}
getters.savedTaskQueries = (state) => {
  return state.user.savedTaskQueries
}

getters.isInGroup = (state) => {
  return (group) => {
    return state.user.groups.includes(group)
  }
}

mutations.userId = (state, id) => {
  state.user.id = id
}

mutations.userGroups = (state, groups) => {
  state.user.groups = groups
}

mutations.userClaims = (state, claims) => {
  state.user.claims = claims
}

mutations.updateGlobalUserPreferences = (state, globalPreferences) => {
  state.user.preferences[Constants.USER_PREFERENCE_TYPES.GLOBAL] = globalPreferences
}

mutations.adminAppPreferences = (state, payload) => {
  state.user.adminAppPreferences[payload.preferenceType] = payload.sortColumns
}

mutations.appendToSavedJobQueries = (state, payload) => {
  state.user.savedJobQueries[Object.keys(payload)[0]] = Object.values(payload)[0]
}

mutations.appendToSavedTaskQueries = (state, payload) => {
  state.user.savedTaskQueries[Object.keys(payload)[0]] = Object.values(payload)[0]
}

mutations.deleteFromSavedJobQueries = (state, queryName) => {
  delete state.user.savedJobQueries[queryName]
}

mutations.deleteFromSavedTaskQueries = (state, queryName) => {
  delete state.user.savedTaskQueries[queryName]
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions: {
    async fetchUserData({ state, dispatch }) {
      const userId = parseActiveDirectoryUserId(state.user.claims.sub)
      state.user.id = userId
      state.user.name = state.user.claims.name
      state.user.username = state.user.claims.name

      await dispatch('fetchAuthenticatedUserPreferences', userId)
      await dispatch('fetchSavedQueries', 'Job')
      await dispatch('fetchSavedQueries', 'Task')
    },
    async fetchSavedQueries({state}, type) {
      const url = `${Constants.ENTITY_ENDPOINTS.USERS}/${state.user.id || parseActiveDirectoryUserId(state.user.claims.sub)}`
      const response = await ptracGetEntity(url)

      if (OK === response.status) {
        const user = response.data
        if (user.hasOwnProperty(`saved${type}Queries`)) {
          state.user[`saved${type}Queries`] = user[`saved${type}Queries`]
        }
      }
    },
    /**
     * This beast of a function makes sure that a user has a value for each possible user preference.
     * That value may be null for String preferences, false for boolean preferences, and zero for Numeric preferences
     * @returns {Promise<void>}
     */
    async fetchAuthenticatedUserPreferences({ state, dispatch }, userId) {
      const response = await ptracGetEntity('users/' + userId)

      if (OK === response.status) {
        const user = response.data
        if (!user.preferences) {
          user.preferences = {}
        }
        if (!user.adminAppPreferences) {
          user.adminAppPreferences = {}
        }

        const allPreferences = await dispatch('users/fetchAllPreferences', null, {root: true})
        for (const preferenceType in allPreferences) {
          const preferencesForType = allPreferences[preferenceType]
          if (!Array.isArray(preferencesForType)) {
            console.error('preferencesForType is not an Array - skipping', {preferenceType, preferencesForType})
            continue
          }

          preferencesForType.forEach(preferencesForType => {
            if (!user.adminAppPreferences.hasOwnProperty(preferenceType)) {
              user.adminAppPreferences[preferenceType] = []
            }

            if (!user.preferences.hasOwnProperty(preferenceType)) {
              user.preferences[preferenceType] = {}
            }
            if (!user.preferences[preferenceType].hasOwnProperty(preferencesForType.key)) {
              user.preferences[preferenceType][preferencesForType.key] = null
              if (preferencesForType.dataType === Constants.DATA_TYPES.BOOLEAN) {
                user.preferences[preferenceType][preferencesForType.key] = false
              } else if (preferencesForType.dataType === Constants.DATA_TYPES.NUMBER) {
                user.preferences[preferenceType][preferencesForType.key] = 0
              }
            } else {
              if (preferencesForType.dataType === Constants.DATA_TYPES.BOOLEAN) {
                user.preferences[preferenceType][preferencesForType.key] = Boolean.parse(user.preferences[preferenceType][preferencesForType.key])
              } else if (preferencesForType.dataType === Constants.DATA_TYPES.NUMBER) {
                user.preferences[preferenceType][preferencesForType.key] = Number(user.preferences[preferenceType][preferencesForType.key])
              }
            }
          })
        }

        state.user.preferences = user.preferences
        state.user.adminAppPreferences = user.adminAppPreferences
      }
    },
    resetAll({commit}) {
      commit('projects/search/resetResults', null, { root: true })
      commit('projects/search/resetQuery', null, { root: true })
      commit('jobs/search/resetResults', null, { root: true })
      commit('jobs/search/resetQuery', null, { root: true })
      commit('tasks/search/resetResults', null,  { root: true })
      commit('tasks/search/resetQuery', null,  { root: true })
      commit('cities/reset', null, { root: true})
      commit('cities/resetResults', null, { root: true})
      commit('communities/reset', null, { root: true})
      commit('communities/resetResults', null, { root: true})
      commit('companies/resetResults', null, { root: true})
      commit('legalSubdivisions/reset', null, { root: true})
      commit('legalSubdivisions/resetResults', null, { root: true})
      commit('priceRules/resetQuery', null, { root: true})
      commit('priceRules/resetResults', null, { root: true})
      commit('streets/resetResults', null, { root: true})
    },
    logout({ commit, dispatch }) {
      localStorage.clear()
      commit('userClaims', {})
      commit('userGroups', [])
      dispatch('resetAll')

      //disable the 'Are you sure you want to navigate away' alert box
      removeEventListener('beforeunload', onUnload)
    }
  }
}
